import React, { useEffect, useState } from 'react';
import { Badge, Tooltip } from '@mui/material';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import { useLocation } from 'react-router-dom';
import { Tune } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { Header } from '../../components/header/header';
import {
  StyledHeaderContainer,
  StyledTitleHeader
} from '../../components/layout/main/mainStyles';
import Strings from '../../i18n';
import { EffectNoVideoTable } from '../../templates/store/effectsStore/effectNoVideoTable/effectNoVideoTable';
import { EffectStoreListTable } from '../../templates/store/effectsStore/effectStoreListTable/effectStoreListTable';
import {
  useGetEffectsReadyForStoreMutation,
  useGetEffectsStoreListMutation,
  useReportEffectMutation,
  useShareEffectMutation
} from '../../services/effects/effectsStoreService';
import { IEffectItemStore } from '../../types/effects/effectsStoreInterface';
import { RightSideBar } from '../../components/layout/rightSideBar/rightSideBar';
import { viewRightSideBarEnum } from '../../constants/sidebarEnums';
import { SingleDetailsEffectStore } from '../../templates/store/effectsStore/singleDetailsEffectStore/singleDetailsEffectStore';
import { ShareEffectDialog } from '../../templates/store/effectsStore/shareEffect/shareEffect';
import { ReportEffectDialog } from '../../templates/store/effectsStore/reportEffect/reportEffect';
import { ViewRatesSideBar } from '../../templates/store/effectsStore/viewRates/viewRates';
import { ContainerTable } from '../../templates/locations/locationsList/locationListStyles';
import { TooltipStyled } from '../locations/locationStyles';
import { ButtonIconStyled } from '../devices/devicesStyles';
import { viewTypeEffect } from '../../constants/commonEnums';
import { TestEffectSideBar } from '../../templates/store/effectsStore/testEffect/testEffectSideBar';
import { EffectStoreFilters } from '../../templates/store/effectsStore/effectStoreFilters/effectStoreFilters';
import { LoadingRowsComponent } from '../../components/commons/LoadingRows/LoadingRows';
import { MultipleDetailsEffectStore } from '../../templates/store/effectsStore/multipleDetailsEffectStore/multipleDetailsEffectsStore';

export const EffectStoreView = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const filtersCount = queryParams.size;
  const filterByDescription = queryParams.get('filterByDescription');
  const filterByStatus = queryParams.get('filterByStatus');
  const filterByType = queryParams.get('filterByType');
  const filterByName = queryParams.get('filterByName');
  const filterByUserName = queryParams.get('filterByUserName');
  const [filters, setFilters] = useState({
    description: filterByDescription || '',
    status: filterByStatus || '',
    type: filterByType || '',
    name: filterByName || '',
    user: filterByUserName || ''
  });

  const [selectedEffects, setSelectedEffects] = React.useState<
    IEffectItemStore[]
  >([]);
  const [currentView, setCurrentView] = useState<viewTypeEffect>(
    viewTypeEffect.effectWithVideo
  );
  const [openSideBar, setOpenSideBar] = React.useState<boolean>(false);
  const [paginator, setPaginator] = useState({
    currentPage: 1,
    pageSize: 250
  });
  const [openShareDialog, setOpenShareDialog] = React.useState<boolean>(false);
  const [openReportDialog, setOpenReportDialog] =
    React.useState<boolean>(false);

  const [shareEffectWithUser, { isSuccess: successShareEffect }] =
    useShareEffectMutation();

  const [sendReportEffect, { isSuccess: successReport }] =
    useReportEffectMutation();

  const shareWithUser = (email: string) => {
    shareEffectWithUser({ effectId: selectedEffects[0].id, email });
  };

  const sendReport = (text: string) => {
    sendReportEffect({ effectId: selectedEffects[0].id, reason: text });
  };
  const [sideBarOpenFor, setSideBarOpenFor] =
    React.useState<viewRightSideBarEnum>(viewRightSideBarEnum.empty);
  const [getEffectsStoreList, { data, isSuccess: successGetEffectsStoreList }] =
    useGetEffectsStoreListMutation();
  const [getEffectsReadyForStore, { data: effectReadyForStore }] =
    useGetEffectsReadyForStoreMutation();

  const reloadTable = (customFilters?: {
    description: string;
    status: string;
    type: string;
    name: string;
    user: string;
  }) => {
    const activeFilters = customFilters || filters;

    getEffectsReadyForStore();
    getEffectsStoreList({
      page: paginator.currentPage,
      size: paginator.pageSize,
      additionalParams: {
        ...activeFilters
      }
    });
    setSelectedEffects([]);
  };

  useEffect(() => {
    const updatedFilters = {
      description: queryParams.get('filterByDescription') || '',
      status: queryParams.get('filterByStatus') || '',
      type: queryParams.get('filterByType') || '',
      name: queryParams.get('filterByName') || '',
      user: queryParams.get('filterByUserName') || '',
      IsDefaultInLocation: queryParams.get('filterByIsDefaultInLoc') || ''
    };
    setFilters(updatedFilters);
    reloadTable(updatedFilters);
  }, [location.search, paginator.currentPage, paginator.pageSize]);

  const reloadTableWithoutRemoveSelectedEffects = () => {
    getEffectsReadyForStore();
    getEffectsStoreList({
      page: paginator.currentPage,
      size: paginator.pageSize,
      additionalParams: {
        ...filters
      }
    });
  };

  const closeSideBar = () => {
    setOpenSideBar(false);
    setSelectedEffects([]);
  };

  const openRightSideBarForMethod = (method: number) => {
    setSideBarOpenFor(method);
    setOpenSideBar(true);
  };

  useEffect(() => {
    if (selectedEffects.length === 1) {
      openRightSideBarForMethod(viewRightSideBarEnum.singleDetailsEffect);
    } else if (selectedEffects.length > 1) {
      openRightSideBarForMethod(viewRightSideBarEnum.multipleDetailsEffect);
    } else {
      openRightSideBarForMethod(viewRightSideBarEnum.empty);
      setOpenSideBar(false);
    }
  }, [selectedEffects]);

  const switchComponentToShowRightSideBar = () => {
    switch (sideBarOpenFor) {
      case viewRightSideBarEnum.singleDetailsEffect:
        return (
          <SingleDetailsEffectStore
            effect={selectedEffects[0]}
            closeSideBar={closeSideBar}
            reloadTable={reloadTable}
            reloadTableWithoutRemoveSelectedEffects={
              reloadTableWithoutRemoveSelectedEffects
            }
            setOpenShareDialog={setOpenShareDialog}
            setOpenReportDialog={setOpenReportDialog}
            openSideBarFor={(value) => openRightSideBarForMethod(value)}
          />
        );
      case viewRightSideBarEnum.viewRates:
        return (
          <ViewRatesSideBar
            closeSideBar={closeSideBar}
            effect={selectedEffects[0]}
          />
        );
      case viewRightSideBarEnum.testEffect:
        return (
          <TestEffectSideBar
            closeSideBar={closeSideBar}
            effect={selectedEffects[0]}
          />
        );
      case viewRightSideBarEnum.effectStoreFilters:
        return (
          <EffectStoreFilters
            reloadTable={reloadTable}
            setOpenRightSideBar={setOpenSideBar}
            openRightSideBar={openSideBar}
          />
        );
      case viewRightSideBarEnum.multipleDetailsEffect:
        return (
          <MultipleDetailsEffectStore
            effects={selectedEffects}
            closeSideBar={closeSideBar}
            reloadTable={reloadTable}
          />
        );
      default:
        return <p />;
    }
  };

  const renderViewEffects = () => {
    switch (currentView) {
      case viewTypeEffect.effectWithVideo:
        return (
          <EffectStoreListTable
            reloadTable={reloadTable}
            selectedEffects={selectedEffects}
            setSelectedEffects={setSelectedEffects}
            data={data?.effects || []}
            totalEffects={data?.totalEffects || 0}
            paginator={paginator}
            setPaginator={(value) => setPaginator(value)}
          />
        );
      case viewTypeEffect.effectWithoutVideo:
        return (
          <EffectNoVideoTable
            selectedEffects={selectedEffects}
            setSelectedEffects={setSelectedEffects}
            reloadTable={reloadTable}
            data={effectReadyForStore || []}
          />
        );
      default:
        return (
          <EffectStoreListTable
            reloadTable={reloadTable}
            selectedEffects={selectedEffects}
            setSelectedEffects={setSelectedEffects}
            data={data?.effects || []}
            paginator={paginator}
            setPaginator={(value) => setPaginator(value)}
            totalEffects={data?.totalEffects || 0}
          />
        );
    }
  };

  useEffect(() => {
    if (successShareEffect) {
      setOpenShareDialog(false);
      toast.success(Strings.effects.shareEffectSuccess);
    }
  }, [successShareEffect]);

  useEffect(() => {
    if (successReport) {
      setOpenReportDialog(false);
      toast.success(Strings.effects.reportEffectSuccess);
    }
  }, [successReport]);

  useEffect(() => {
    setFilters({
      description: filterByDescription || '',
      status: filterByStatus || '',
      type: filterByType || '',
      name: filterByName || '',
      user: filterByUserName || ''
    });
  }, [
    filterByDescription,
    filterByStatus,
    filterByType,
    filterByName,
    filterByUserName
  ]);

  useEffect(() => {
    if (successGetEffectsStoreList) {
      const updatedSelectedEffects = selectedEffects.filter((selected) =>
        data?.effects.some((effect) => effect.id === selected.id)
      );
      setSelectedEffects(updatedSelectedEffects);
    }
  }, [successGetEffectsStoreList, data]);

  return (
    <>
      <Header>
        <StyledHeaderContainer>
          <div className="row justify-content-between">
            <div className="col-1 col-sm-4 text-left" />
            <div className="col-6 col-sm-4 text-center">
              <StyledTitleHeader align="center">
                {Strings.effects.effectsStore}
              </StyledTitleHeader>
            </div>
            <div className="col-5 col-sm-4 text-right">
              <Tooltip
                title={Strings.locations.effectWithVideo}
                placement="top"
                arrow
                componentsProps={TooltipStyled}
              >
                <ButtonIconStyled
                  className={
                    viewTypeEffect.effectWithVideo === currentView
                      ? 'selected'
                      : ''
                  }
                  size="small"
                  onClick={() => {
                    setCurrentView(viewTypeEffect.effectWithVideo);
                  }}
                >
                  <VideocamIcon />
                </ButtonIconStyled>
              </Tooltip>
              <Tooltip
                title={Strings.locations.effectWithoutVideo}
                placement="top"
                arrow
                componentsProps={TooltipStyled}
              >
                <ButtonIconStyled
                  className={
                    viewTypeEffect.effectWithoutVideo === currentView
                      ? 'selected'
                      : ''
                  }
                  size="small"
                  onClick={() => {
                    setCurrentView(viewTypeEffect.effectWithoutVideo);
                  }}
                >
                  <VideocamOffIcon />
                </ButtonIconStyled>
              </Tooltip>
              <ButtonIconStyled
                size="small"
                onClick={() => {
                  openRightSideBarForMethod(
                    viewRightSideBarEnum.effectStoreFilters
                  );
                }}
              >
                <Badge
                  badgeContent={filtersCount}
                  color="warning"
                  invisible={filtersCount === 0}
                >
                  <Tune />
                </Badge>
              </ButtonIconStyled>
            </div>
          </div>
        </StyledHeaderContainer>
      </Header>
      <ContainerTable component="main">
        {data ? renderViewEffects() : <LoadingRowsComponent />}
      </ContainerTable>
      <RightSideBar
        open={openSideBar}
        onMenuClose={() => {
          closeSideBar();
        }}
      >
        {switchComponentToShowRightSideBar()}
      </RightSideBar>
      <ShareEffectDialog
        open={openShareDialog}
        setOpen={setOpenShareDialog}
        onShare={(email) => {
          shareWithUser(email);
        }}
      />
      <ReportEffectDialog
        reportHandler={(text: string) => sendReport(text)}
        open={openReportDialog}
        setOpen={setOpenReportDialog}
      />
    </>
  );
};
